// Phase 23B.5 — SE mobile Add-Outlet screen (offline-capable, storefront-photo aware)
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, MapPin, Camera, Store, ArrowLeft, Send, RefreshCw, Wifi, WifiOff } from "lucide-react";
import { api, API_BASE, getToken, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { mutate, isOnline, getSimulateOffline } from "@/offline/apiClient";
import { putBlob, getBlob, deleteBlob, BlobCapError } from "@/offline/outboxBlobs";
import * as outbox from "@/offline/outbox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import RetailerDetailsSheet from "@/components/RetailerDetailsSheet";

const CFG_CACHE_KEY = "outlet_cfg_v1";
const CFG_TTL_MS = 5 * 60 * 1000;   // 5 minutes


async function compressJpeg(file, maxDim = 1280, quality = 0.75) {
  const img = await new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = URL.createObjectURL(file);
  });
  const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
  const w = Math.round(img.width * scale);
  const h = Math.round(img.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  canvas.getContext("2d").drawImage(img, 0, 0, w, h);
  return await new Promise(r => canvas.toBlob(r, "image/jpeg", quality));
}

async function uploadPhoto(blob) {
  const fd = new FormData();
  fd.append("file", blob, `storefront-${Date.now()}.jpg`);
  fd.append("context", "outlet_storefront");
  const res = await fetch(`${API_BASE}/photos/upload`, {
    method: "POST", body: fd,
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  if (!res.ok) throw new Error(`upload failed: ${res.status}`);
  return await res.json();
}

function loadCachedConfig() {
  try {
    const raw = localStorage.getItem(CFG_CACHE_KEY);
    if (!raw) return null;
    const { cfg, ts } = JSON.parse(raw);
    if (Date.now() - ts > CFG_TTL_MS) return { ...cfg, stale: true };
    return cfg;
  } catch { return null; }
}

function saveCachedConfig(cfg) {
  try { localStorage.setItem(CFG_CACHE_KEY, JSON.stringify({ cfg, ts: Date.now() })); } catch {}
}


export default function AddOutlet() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const editDraftId = sp.get("draft");        // outbox _id (number/string) when editing
  const isDist = user?.role_code === "DISTRIBUTOR";
  // Opened from "New Call" (Today's Beat for SE/ASM, the portal Beat screen
  // for a distributor) — return to whichever one launched it, instead of
  // the (unlinked, SE/ASM-only) My Outlets list.
  const backTarget = sp.get("from") === "new-call"
    ? (isDist ? "/portal/beat" : "/today-beat")
    : "/outlets";

  // --- State ---------------------------------------------------------------
  const [cfg, setCfg] = useState(() => loadCachedConfig() || { require_storefront_photo: false });
  const [cfgLoading, setCfgLoading] = useState(true);
  const [outletTypes, setOutletTypes] = useState([]);
  const [beats, setBeats] = useState([]);
  const [todayBeatId, setTodayBeatId] = useState("");
  const [distributors, setDistributors] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  const [name, setName] = useState("");
  const [outletType, setOutletType] = useState("");
  const [address, setAddress] = useState("");
  const [contactPerson, setContactPerson] = useState("");
  const [phone, setPhone] = useState("");
  // A distributor has no PJP to default the beat from (the SE/ASM path
  // below), so the portal Beat screen passes its own current selection
  // through instead — pre-fills the same way, still editable if they're
  // mapped to more than one beat.
  const [beatId, setBeatId] = useState(() => sp.get("beat_id") || "");
  const [distributorId, setDistributorId] = useState("");
  const [stateId, setStateId] = useState("");
  const [cityId, setCityId] = useState("");

  // Duplicate-phone guard — same phone already linked to another outlet.
  const [dupCheckBusy, setDupCheckBusy] = useState(false);
  const [dupOutlet, setDupOutlet] = useState(null);      // matched retailer doc, or null
  const [dupPopupOpen, setDupPopupOpen] = useState(false);
  const [dupDetailsOpen, setDupDetailsOpen] = useState(false);

  const [gps, setGps] = useState(null);        // {lat, lng, accuracy_m}
  const [gpsErr, setGpsErr] = useState(null);
  const [gpsBusy, setGpsBusy] = useState(false);

  const [photo, setPhoto] = useState(null);    // {blob, url, ref?, capturedAt}
  const [photoBusy, setPhotoBusy] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const nameRef = useRef(null);
  const addressRef = useRef(null);
  const draftClientTempId = useRef(null);   // set when editing to keep same idempotency

  // Phase 23B.5.1 — draft-edit prefill from queued outbox row
  useEffect(() => {
    if (!editDraftId) return;
    (async () => {
      try {
        const rows = await outbox.listAll();
        const row = rows.find(r => String(r._id) === String(editDraftId));
        if (!row) { toast.error("Draft not found"); return; }
        if (row.status === "in_flight") {
          toast.error("Draft is currently syncing — try again in a moment");
          navigate(backTarget, { replace: true });
          return;
        }
        const b = row.body || {};
        setName(b.name || "");
        setOutletType(b.outlet_type_lookup_id || "");
        setAddress(b.address || "");
        setContactPerson(b.owner_name || "");
        setPhone(b.phone || "");
        if (b.city_id) setCityId(b.city_id);
        if (b.beat_id) setBeatId(b.beat_id);
        if (b.distributor_id) setDistributorId(b.distributor_id);
        if (b.gps_lat && b.gps_lng) setGps({ lat: b.gps_lat, lng: b.gps_lng, accuracy_m: 0 });
        draftClientTempId.current = b.client_temp_id || null;
        // Restore blob preview if we have one
        if (b.client_temp_id) {
          const br = await getBlob(b.client_temp_id).catch(() => null);
          if (br?.blob) {
            setPhoto({
              blob: br.blob,
              url: URL.createObjectURL(br.blob),
              ref: b.storefront_photo_ref || null,
              capturedAt: br.created_at,
            });
          } else if (b.storefront_photo_ref) {
            setPhoto({ blob: null, url: null, ref: b.storefront_photo_ref,
                        capturedAt: b.storefront_photo_meta?.captured_at_ts });
          }
        }
      } catch (e) { toast.error(formatApiError(e)); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editDraftId]);

  // --- Config, lookups, beats, distributors, today's PJP ------------------
  useEffect(() => {
    // Config (cached, then refresh from network)
    api.get("/outlet-config").then(r => {
      setCfg(r.data); saveCachedConfig(r.data);
    }).catch(() => { /* keep cached */ })
      .finally(() => setCfgLoading(false));

    api.get("/lookups?category=outlet_type").then(r => {
      const items = (r.data?.items || r.data || []).filter(x => x.is_active !== false);
      setOutletTypes(items);
    }).catch(() => {});

    // `scope=mine` — SE is always forced to their own beats server-side
    // regardless of this param, but a manager (ASM/RSM/...) needs it
    // explicitly to narrow to their team's beats instead of every beat in
    // the zone. The old client-side filter here only handled the SE case
    // and let ASM see the whole zone's beats, mapped to them or not.
    api.get("/beats", { params: { page_size: 200, scope: "mine" } }).then(r => {
      setBeats(r.data.items || []);
    }).catch(() => {});

    // Today's effective PJP → default beat
    if (user?.id) {
      const today = new Date().toISOString().slice(0, 10);
      api.get("/pjp/calendar", { params: { user_id: user.id, month: today.slice(0, 7) } })
        .then(r => {
          const cur = (r.data?.days || []).find(d => d.date === today);
          if (cur?.beat_id) {
            setTodayBeatId(cur.beat_id);
            setBeatId(cur.beat_id);
          }
        }).catch(() => {});
    }

    // Distributors (SE sees only mapped ones; admin sees all)
    api.get("/distributors", { params: { page_size: 2000 } })
      .then(r => setDistributors(r.data.items || []))
      .catch(() => {});

    api.get("/states").then(r => setStates(r.data?.items || r.data || [])).catch(() => {});
  }, [user?.id, user?.role_code]);

  // City list cascades from the selected State; clear a stale city selection
  // when its state no longer matches.
  useEffect(() => {
    if (!stateId) { setCities([]); return; }
    api.get("/cities", { params: { state_id: stateId } })
      .then(r => setCities(r.data?.items || r.data || []))
      .catch(() => setCities([]));
  }, [stateId]);

  // Distributor follows the selected beat — a beat is served by exactly one
  // distributor (distributor.beat_ids), so this isn't a free choice the
  // field user should be making themselves; it's derived. Re-derive
  // whenever the beat selection or the distributor list changes (e.g. once
  // the /distributors fetch above resolves after beatId was already set
  // from today's PJP default).
  const selectedBeat = beats.find(b => b.id === beatId);
  const linkedDistributor = distributors.find(d => (d.beat_ids || []).includes(beatId))
    // fall back to the distributor the server names for this beat
    || (selectedBeat?.distributor_id
        ? { id: selectedBeat.distributor_id, code: selectedBeat.distributor_code, name: selectedBeat.distributor_name }
        : null);
  useEffect(() => {
    setDistributorId(linkedDistributor?.id || "");
  }, [linkedDistributor?.id]);

  // GPS auto-capture on mount
  const captureGps = useCallback(() => {
    if (!navigator.geolocation) { setGpsErr("GPS unavailable"); return; }
    setGpsBusy(true);
    navigator.geolocation.getCurrentPosition(
      p => {
        setGps({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy_m: p.coords.accuracy });
        setGpsErr(null); setGpsBusy(false);
      },
      err => { setGpsErr(err.message || "GPS denied"); setGpsBusy(false); },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 5000 },
    );
  }, []);
  useEffect(() => { captureGps(); }, [captureGps]);

  // --- Photo capture (same pattern as attendance selfie) -------------------
  const capturePhoto = () => {
    const input = document.createElement("input");
    input.type = "file"; input.accept = "image/*"; input.capture = "environment";
    input.onchange = async () => {
      const f = input.files?.[0]; if (!f) return;
      setPhotoBusy(true);
      try {
        const small = await compressJpeg(f);
        const url = URL.createObjectURL(small);
        const capturedAt = new Date().toISOString();
        setPhoto({ blob: small, url, ref: null, capturedAt });
        if (isOnline() && !getSimulateOffline()) {
          const r = await uploadPhoto(small);
          setPhoto(p => ({ ...p, ref: r.ref }));
          toast.success("Storefront photo captured");
        } else {
          // Phase 23B.5.1 — buffer blob under a stable client_temp_id.
          if (!draftClientTempId.current) {
            draftClientTempId.current = crypto.randomUUID
              ? crypto.randomUUID()
              : "tmp-" + Date.now() + "-" + Math.random().toString(36).slice(2, 8);
          }
          try {
            await putBlob({
              client_temp_id: draftClientTempId.current,
              blob: small,
              blob_type: "storefront_photo",
            });
            toast.info("Storefront captured (will upload on sync)");
          } catch (be) {
            setPhoto(null);
            if (be instanceof BlobCapError) toast.error(be.message);
            else toast.error("Failed to buffer photo offline");
          }
        }
      } catch (e) {
        toast.error(e.message || "Photo capture failed");
      } finally { setPhotoBusy(false); }
    };
    input.click();
  };

  // --- Validation ----------------------------------------------------------
  const nameOk = name.trim().length >= 2;
  const outletTypeOk = !!outletType;
  const addressOk = address.trim().length >= 3;
  const beatOk = !!beatId;
  const phoneOk = !phone || /^\+?[1-9]\d{7,14}$/.test(phone.replace(/[\s-]/g, ""));
  const photoOk = !cfg?.require_storefront_photo || !!photo?.blob;
  const noDuplicate = !dupOutlet;
  const allOk = nameOk && outletTypeOk && addressOk && beatOk && phoneOk && photoOk && noDuplicate;

  const focusFirstInvalid = () => {
    if (!nameOk) { nameRef.current?.focus(); return true; }
    if (!addressOk) { addressRef.current?.focus(); return true; }
    return false;
  };

  // Duplicate-phone guard — fires on blur of the phone field. If another
  // outlet already has this number, block submission and surface its
  // details instead of silently letting a duplicate outlet get created.
  const checkDuplicatePhone = async () => {
    const digits = phone.replace(/\D/g, "");
    if (digits.length < 8) { setDupOutlet(null); return; }
    setDupCheckBusy(true);
    try {
      const { data } = await api.get("/retailers", { params: { phone_exact: phone, page_size: 1 } });
      const match = (data.items || [])[0] || null;
      setDupOutlet(match);
      if (match) setDupPopupOpen(true);
    } catch (_e) {
      // Best-effort — don't block the form if the check itself fails.
    } finally {
      setDupCheckBusy(false);
    }
  };

  // --- Submit --------------------------------------------------------------
  const doSubmit = async () => {
    if (!allOk) {
      focusFirstInvalid();
      if (!photoOk) toast.error("Storefront photo required");
      else toast.error("Fix the highlighted fields");
      return;
    }
    setSubmitting(true);
    try {
      // If online + photo not yet uploaded, upload now
      let photoRef = photo?.ref || null;
      if (photo && !photoRef && isOnline() && !getSimulateOffline()) {
        try {
          const r = await uploadPhoto(photo.blob);
          photoRef = r.ref;
        } catch (e) {
          if (cfg?.require_storefront_photo) throw e;
        }
      }

      const client_temp_id = draftClientTempId.current || (crypto.randomUUID
        ? crypto.randomUUID()
        : "tmp-" + Date.now() + "-" + Math.random().toString(36).slice(2, 8));
      draftClientTempId.current = client_temp_id;

      const body = {
        name: name.trim(),
        outlet_type_lookup_id: outletType,
        address: address.trim(),
        owner_name: contactPerson.trim() || undefined,
        phone: phone.trim() || undefined,
        city_id: cityId || undefined,
        beat_id: beatId,
        distributor_id: distributorId || undefined,
        gps_lat: gps?.lat, gps_lng: gps?.lng,
        location_accuracy_m: gps?.accuracy_m ?? undefined,
        has_gps: !!gps,
        storefront_photo_ref: photoRef || undefined,
        storefront_photo_meta: photo ? {
          gps: gps ? { lat: gps.lat, lng: gps.lng } : null,
          captured_at_ts: photo.capturedAt,
          uploaded_at_ts: new Date().toISOString(),
          uploader_user_id: user?.id,
        } : undefined,
        client_temp_id,
      };

      // If we're editing an existing queued draft, update the same row
      // (keep the client_temp_id / idempotency_key) rather than posting
      // a fresh mutation.
      if (editDraftId) {
        await outbox.updateRow(Number(editDraftId), {
          body,
          status: outbox.STATUS.PENDING,
          last_error: null,
        });
        toast.success("Draft updated");
        navigate(backTarget, { replace: true });
        return;
      }

      const result = await mutate({
        method: "POST",
        path: "/retailers",
        body,
        entity_type: "retailers_outlets",
        owner_user_id: user?.id,
        idempotency_key: client_temp_id,
      });

      if (result.immediate) {
        toast.success("Outlet added");
      } else {
        toast.success("Outlet queued (pending sync)");
      }
      navigate(backTarget, { replace: true });
    } catch (e) {
      // Server 422 storefront_photo_required (belt-and-braces from 23B backend)
      const msg = e?.status === 422 && e?.response?.data?.detail?.error === "storefront_photo_required"
        ? "Storefront photo is required by policy"
        : (e?.message || "Failed to add outlet");
      toast.error(msg);
    } finally { setSubmitting(false); }
  };

  const online = isOnline() && !getSimulateOffline();
  const defaultedBeat = beats.find(b => b.id === todayBeatId);

  return (
    <div className="p-3 sm:p-4 space-y-3 pb-40" data-testid="add-outlet-page">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}
                 data-testid="add-outlet-back">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-lg font-bold text-neutral-900 flex items-center gap-2">
            <Store className="h-5 w-5 text-[#6D2C91]" /> Add Outlet
          </h1>
          <p className="text-[11px] text-neutral-500">
            {online ? <Wifi className="h-3 w-3 inline mr-1" /> : <WifiOff className="h-3 w-3 inline mr-1" />}
            {online ? "Online — submits directly" : "Offline — will queue for sync"}
            {cfg?.stale && <span className="ml-1 text-amber-600">· cached policy</span>}
          </p>
        </div>
      </div>

      <Card className="border-neutral-200 rounded-2xl">
        <CardContent className="p-3 space-y-3">
          <Field label="Outlet name" required error={name && !nameOk} testid="add-outlet-name-field">
            <Input ref={nameRef} value={name} onChange={e => setName(e.target.value)}
                    placeholder="e.g. Sharma General Store"
                    data-testid="add-outlet-name" />
          </Field>

          <Field label="Outlet type" required error={!outletType} testid="add-outlet-type-field">
            <select value={outletType} onChange={e => setOutletType(e.target.value)}
                     className="w-full border rounded-md p-2 text-sm bg-white"
                     data-testid="add-outlet-type">
              <option value="">— pick type —</option>
              {outletTypes.map(o => <option key={o.id} value={o.id}>{o.label || o.code}</option>)}
            </select>
          </Field>

          <Field label="Address" required error={address && !addressOk} testid="add-outlet-address-field">
            <Textarea ref={addressRef} value={address} onChange={e => setAddress(e.target.value)}
                       rows={2} placeholder="Street, area, landmark"
                       data-testid="add-outlet-address" />
          </Field>

          <Field label="Owner name" testid="add-outlet-contact-field">
            <Input value={contactPerson} onChange={e => setContactPerson(e.target.value)}
                    placeholder="Optional"
                    data-testid="add-outlet-contact" />
          </Field>

          <Field label="Outlet phone number" error={phone && !phoneOk} testid="add-outlet-phone-field">
            <Input value={phone} onChange={e => { setPhone(e.target.value); setDupOutlet(null); }}
                    onBlur={checkDuplicatePhone}
                    placeholder="+91…"
                    data-testid="add-outlet-phone" />
            {dupCheckBusy && (
              <div className="text-[10px] text-neutral-400 mt-0.5">Checking for existing outlet…</div>
            )}
          </Field>

          <div className="grid grid-cols-2 gap-2">
            <Field label="State" testid="add-outlet-state-field">
              <select value={stateId}
                       onChange={e => { setStateId(e.target.value); setCityId(""); }}
                       className="w-full border rounded-md p-2 text-sm bg-white"
                       data-testid="add-outlet-state">
                <option value="">— optional —</option>
                {states.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </Field>
            <Field label="City" testid="add-outlet-city-field">
              <select value={cityId} onChange={e => setCityId(e.target.value)}
                       disabled={!stateId}
                       className="w-full border rounded-md p-2 text-sm bg-white disabled:opacity-50"
                       data-testid="add-outlet-city">
                <option value="">{stateId ? "— optional —" : "pick a state first"}</option>
                {cities.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Field>
          </div>

          <Field label="Linked beat" required error={!beatId} testid="add-outlet-beat-field">
            <select value={beatId} onChange={e => setBeatId(e.target.value)}
                     className="w-full border rounded-md p-2 text-sm bg-white"
                     data-testid="add-outlet-beat">
              <option value="">— pick beat —</option>
              {beats.map(b => (
                <option key={b.id} value={b.id}>
                  {`${b.code} · ${b.name}${b.id === todayBeatId ? " (today)" : ""}`}
                </option>
              ))}
            </select>
            {defaultedBeat && beatId === todayBeatId && (
              <div className="text-[10px] text-emerald-600 mt-0.5" data-testid="add-outlet-beat-today-hint">
                Defaulted from today's PJP · {defaultedBeat.code}
              </div>
            )}
          </Field>

          <Field label="Linked distributor" testid="add-outlet-dist-field">
            <div className="w-full border rounded-md p-2 text-sm bg-neutral-50 text-neutral-700"
                 data-testid="add-outlet-dist">
              {linkedDistributor
                ? `${linkedDistributor.code} · ${linkedDistributor.name}`
                : beatId
                  ? "No distributor mapped to this beat"
                  : "— pick a beat first —"}
            </div>
            <div className="text-[10px] text-neutral-400 mt-0.5">
              Auto-set from the linked beat's mapped distributor
            </div>
          </Field>
        </CardContent>
      </Card>

      {/* GPS */}
      <Card className="border-neutral-200 rounded-2xl">
        <CardContent className="p-3 flex items-center gap-3">
          <MapPin className="h-5 w-5 text-[#6D2C91]" />
          <div className="flex-1 text-xs">
            <div className="font-semibold text-neutral-900">GPS location</div>
            {gps ? (
              <div className="font-mono text-neutral-700" data-testid="add-outlet-gps-chip">
                {gps.lat.toFixed(4)}, {gps.lng.toFixed(4)}
                <span className="ml-1 text-neutral-400">±{Math.round(gps.accuracy_m)}m</span>
              </div>
            ) : gpsErr ? (
              <div className="text-amber-700" data-testid="add-outlet-gps-err">{gpsErr} — submit will flag has_gps=false</div>
            ) : (
              <div className="text-neutral-500">Capturing…</div>
            )}
          </div>
          <Button size="sm" variant="outline" onClick={captureGps} disabled={gpsBusy}
                   data-testid="add-outlet-gps-btn">
            {gpsBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          </Button>
        </CardContent>
      </Card>

      {/* Storefront photo */}
      <Card className={"border rounded-2xl " + (!photoOk ? "border-rose-300 bg-rose-50/40" : "border-neutral-200")}
             data-testid="add-outlet-storefront-card">
        <CardContent className="p-3 space-y-2">
          <div className="flex items-center gap-2">
            <Camera className="h-4 w-4 text-[#6D2C91]" />
            <div className="flex-1">
              <div className="text-sm font-semibold text-neutral-900">
                Storefront photo
                {cfg?.require_storefront_photo
                  ? <span className="text-rose-600 ml-1" data-testid="add-outlet-photo-required">*</span>
                  : <span className="text-neutral-400 ml-1 text-xs">(optional)</span>}
              </div>
              <div className="text-[10px] text-neutral-500">Captures photo + GPS + timestamp</div>
            </div>
            <Button size="sm" variant={photo ? "outline" : "default"}
                     className={!photo ? "bg-[#6D2C91] text-white hover:bg-[#5b2478]" : ""}
                     onClick={capturePhoto} disabled={photoBusy}
                     data-testid="add-outlet-photo-btn">
              {photoBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Camera className="h-3.5 w-3.5 mr-1" />}
              {photo ? "Retake" : "Capture"}
            </Button>
          </div>
          {photo && (
            <div className="rounded-lg overflow-hidden bg-neutral-100 h-40 relative"
                  data-testid="add-outlet-photo-preview">
              <img src={photo.url} alt="storefront" className="w-full h-full object-cover" />
              {!photo.ref && (
                <div className="absolute top-1 right-1 bg-amber-100 text-amber-800 text-[10px] px-2 py-0.5 rounded-full">
                  pending upload
                </div>
              )}
            </div>
          )}
          {!photoOk && (
            <div className="text-[11px] text-rose-600" data-testid="add-outlet-photo-error">
              Storefront photo is required by policy.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Fixed submit — sits ABOVE the mobile shell's bottom nav (h-16, z-30),
          which otherwise renders on top of and hides a bottom-0 bar here. */}
      <div className="fixed bottom-16 left-0 right-0 p-3 bg-white border-t border-neutral-200 max-w-md mx-auto z-40">
        <Button className="w-full h-12 bg-[#6D2C91] text-white hover:bg-[#5b2478]"
                 disabled={!allOk || submitting}
                 onClick={doSubmit}
                 data-testid="add-outlet-submit">
          {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
          {online ? "Submit outlet" : "Queue for sync"}
        </Button>
        {!allOk && (
          <div className="text-[10px] text-neutral-500 text-center mt-1" data-testid="add-outlet-block-reason">
            {!nameOk && "Name · "} {!outletTypeOk && "Type · "} {!addressOk && "Address · "}
            {!beatOk && "Beat · "} {!phoneOk && "Phone · "} {!photoOk && "Photo · "}
            {!noDuplicate && "Duplicate phone · "}
            required
          </div>
        )}
      </div>

      {/* Duplicate-phone popup — this number already belongs to an outlet. */}
      <Dialog open={dupPopupOpen} onOpenChange={setDupPopupOpen}>
        <DialogContent className="max-w-sm" data-testid="add-outlet-dup-popup">
          <DialogHeader>
            <DialogTitle>Can't add — outlet already exists</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-neutral-700">
            This phone number is already saved against an outlet:
          </div>
          {dupOutlet && (
            <div className="rounded-xl border border-neutral-200 p-3 space-y-0.5">
              <div className="text-[11px] text-neutral-400 font-mono">{dupOutlet.code}</div>
              <div className="text-sm font-semibold text-neutral-900">{dupOutlet.name}</div>
              <div className="text-xs text-neutral-500">{dupOutlet.address || "—"}</div>
              {dupOutlet.owner_name && (
                <div className="text-xs text-neutral-500">Owner: {dupOutlet.owner_name}</div>
              )}
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setDupPopupOpen(false)}
                    data-testid="add-outlet-dup-edit-phone">
              Change number
            </Button>
            <Button onClick={() => { setDupPopupOpen(false); setDupDetailsOpen(true); }}
                    className="bg-[#6D2C91] text-white"
                    data-testid="add-outlet-dup-view">
              View outlet details
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <RetailerDetailsSheet
        retailerId={dupOutlet?.id}
        open={dupDetailsOpen}
        onOpenChange={setDupDetailsOpen}
      />
    </div>
  );
}


function Field({ label, required, error, children, testid }) {
  return (
    <div data-testid={testid}>
      <Label className="text-xs text-neutral-500 flex items-center gap-1">
        {label}
        {required && <span className="text-rose-600" data-testid={testid + "-asterisk"}>*</span>}
      </Label>
      <div className={error ? "ring-1 ring-rose-300 rounded" : ""}>
        {children}
      </div>
      {error && (
        <div className="text-[10px] text-rose-600 mt-0.5">Required or invalid</div>
      )}
    </div>
  );
}
